import { datadogLogs } from '@datadog/browser-logs';
import { HsClassificationCalculateResponse, ICustomsService, IHsCodeAlternate } from '../@posturinn/client-react';
import {
	SEARCH_HS_CODE_FAILED,
	SEARCH_HS_CODE_FULFILLED,
	SEARCH_HS_CODE_PENDING,
} from '../constants/actions/customsActions';
import { CLIENT_ID } from '../helpers/mobizHelpers/configurations';

export const setHsCodeLookupResults = (results: IHsCodeAlternate[] | null) => ({
	type: SEARCH_HS_CODE_FULFILLED,
	payload: results,
});

/**
 * Searches the Mobiz duties API for HS codes matching the given term. The search results are
 * de-duplicated on the 6-digit subheading code and their fragments are reversed so that the
 * broadest fragment (chapter) comes first.
 */
export const searchHsCode = (
	name: string,
	category: string = null,
	recipientCountry: string = null,
	description: string = null,
	countryOfOrigin: string = null
) => {
	return async (dispatch, getState) => {
		// Capture the results currently on screen before starting the new search, so we can log
		// them for context if this search fails.
		const previousResults = getState().customsReducer.hsCodeSearchResults;

		dispatch({ type: SEARCH_HS_CODE_PENDING });

		const isNumeric = /^\d+$/.test(name.trim());
		const configuration = {
			...(recipientCountry && { shipToCountries: recipientCountry }),
			...(isNumeric && { hsCodeProvided: name }),
		};

		try {
			const { injector } = getState().mobizReducer;
			const customsService: ICustomsService = injector.get(ICustomsService);
			const { isLoggedIn } = getState().userProfileReducer;

			const params = {
				name,
				categories: category,
				description,
				configuration: Object.keys(configuration).length > 0 ? configuration : null,
				countryOfOrigin,
			};
			// Authenticated flow hits the account-scoped endpoint; the anonymous register-shipment
			// flow uses the account-less /calculate-duties endpoint.
			const search$ = isLoggedIn
				? customsService.searchHsCode(params)
				: customsService.searchHsCodeForAnonymous(params);

			search$.subscribe({
				next: (response: HsClassificationCalculateResponse) => {
					// [0] because calculate-duties always returns a single classification object
					const alternates = response.data?.classificationsCalculate?.[0]?.alternates ?? [];
					const uniqueCodes = new Set<string>();
					const uniqueAlternates = alternates.filter(alternate => {
						// Only process alternates with at least 3 fragments to prevent index errors.
						if (alternate.subheadingAlternate.fragments.length < 3) return false;
						// The first fragment is the last section of the HS code, so reverse to get
						// chapter -> heading -> subheading order.
						alternate.subheadingAlternate.fragments.reverse();
						const code = alternate.subheadingAlternate.fragments[2].code;
						if (uniqueCodes.has(code)) return false;
						uniqueCodes.add(code);
						return true;
					});

					// Log queries and results so we can understand what users search for.
					datadogLogs.logger.log(`HS code lookup results for: ${name} - ${category}`, {
						searchTerm: name,
						searchCategory: category,
						searchDescription: description,
						results: uniqueAlternates,
					});

					dispatch(setHsCodeLookupResults(uniqueAlternates));
				},
				error: (error: any) => {
					// Anonymous callers can be rate-limited: a 403 "Too many requests." gets its own message.
					const throttled =
						error?.status === 403 &&
						`${error?.message ?? ''} ${error?.info?.error ?? ''}`
							.toLowerCase()
							.includes('too many requests');
					dispatch({
						type: SEARCH_HS_CODE_FAILED,
						payload: throttled
							? 'step-export.hsCodeLookup.throttled'
							: 'step-export.hsCodeLookup.searchError',
					});
					datadogLogs.logger.error(
						`HS Code lookup ${throttled ? 'throttled' : 'failed'} - "${name}" - ${category}`,
						{
							clientId: CLIENT_ID,
							searchTerm: name,
							searchCategory: category,
							throttled,
							previousResults,
						},
						error
					);
				},
			});
		} catch {
			dispatch({ type: SEARCH_HS_CODE_FAILED, payload: 'step-export.hsCodeLookup.searchError' });
		}
	};
};
